/**
 * hk_os · <VoiceWarmupManager>
 * ────────────────────────────────────────────────────────────────────
 * Renders a compact readiness widget AND drives the background warm-up:
 *  1. Fetches the JSON manifest via the VoiceRegistry provider (once).
 *  2. Compares stored checksum → downloads a new manifest.zip only when
 *     the aggregate checksum changed.
 *  3. Populates the browser Cache API with every audio URL so playback
 *     works offline (Cache-first strategy).
 *
 * Never blocks the operational UI. Users never trigger this manually.
 * The widget can be placed anywhere (typically in an app-shell footer or
 * settings drawer).
 *
 * Props:
 *   auto            — auto-warmup on first render (default true)
 *   hideWhenReady   — collapse to a dot when everything is cached (default true)
 *   testid
 */
import React, { useEffect, useState } from "react";
import {
  CloudDownload, CheckCircle2, WifiOff, Loader2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

function fmtVersion(meta) {
  if (!meta) return "—";
  return `v${meta.version} · ${meta.checksum?.slice(0, 6) || ""}`;
}

export function VoiceWarmupManager({
  auto = true,
  hideWhenReady = true,
  testid,
}) {
  const {
    manifest, meta, status, warmedCount, totalCount, isOffline,
    refreshWarmup, reload,
  } = useVoiceRegistry();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Nothing to do — provider already runs on mount when autoWarmup=true.
    void auto;
  }, [auto]);

  const pct = totalCount === 0 ? 0 : warmedCount / totalCount;
  const ready = status !== "loading" && warmedCount > 0 && warmedCount === totalCount;

  const onRefresh = async () => {
    setBusy(true);
    try {
      await reload();
      await refreshWarmup();
    } finally {
      setBusy(false);
    }
  };

  if (ready && hideWhenReady) {
    return (
      <span
        data-testid={testid || "voice-warmup-ready"}
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-2.5 py-1 text-[11px] font-semibold"
        title={`Voice pack cached · ${fmtVersion(meta)}`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
        Voice ready
      </span>
    );
  }

  let Icon = CloudDownload;
  let tone = "bg-sky-50 border-sky-300 text-sky-900";
  let label = "Downloading voice pack";
  if (status === "loading" || busy) {
    Icon = Loader2; label = "Warming voice cache";
  } else if (isOffline) {
    Icon = WifiOff; tone = "bg-amber-50 border-amber-300 text-amber-900"; label = "Offline · using cache";
  } else if (status === "error") {
    Icon = AlertTriangle; tone = "bg-red-50 border-red-300 text-red-800"; label = "Voice pack unavailable";
  } else if (ready) {
    Icon = CheckCircle2; tone = "bg-emerald-50 border-emerald-300 text-emerald-800"; label = "Voice pack ready";
  }

  return (
    <div
      data-testid={testid || "voice-warmup-manager"}
      role="status"
      className={`inline-flex flex-col gap-1.5 rounded-2xl border-2 ${tone} px-3 py-2 min-w-[220px]`}
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold">
        <Icon
          className={`w-4 h-4 ${status === "loading" || busy ? "animate-spin" : ""}`}
          strokeWidth={2.4}
        />
        <span className="truncate">{label}</span>
        <span className="ml-auto text-[11px] font-medium opacity-75 tabular-nums">
          {warmedCount}/{totalCount || (manifest?.snippets?.length ?? 0)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/80 overflow-hidden">
        <div
          data-testid={testid ? `${testid}-progress` : "voice-warmup-progress"}
          className="h-full bg-current transition-[width] duration-300"
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] opacity-80">
        <span>{fmtVersion(meta)}</span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          data-testid={testid ? `${testid}-refresh` : "voice-warmup-refresh"}
          className="inline-flex items-center gap-1 rounded-full border border-current px-2 py-0.5 hover:bg-white/50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} strokeWidth={2.4} />
          Refresh
        </button>
      </div>
    </div>
  );
}

export default VoiceWarmupManager;
